import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { User } from 'src/app/services/auth.service';
import { Domain } from 'src/app/services/domain.service';
import { AiQuotaStatus, AiService } from 'src/app/services/ai.service';
import { AiOverlayService } from 'src/app/services/ai-overlay.service';
import { SearchStateService } from 'src/app/services/search-state.service';
import { selectDomain, selectRoles, selectUser } from 'src/app/store/selectors/auth.selectors';
import { canUseAiAssistant } from 'src/app/utils/ai-access';

/**
 * Steps of the guided tour of the assistant.
 *
 * - `search`: on the home page, the bar holds the sample question.
 * - `answer`: in the assistant, a banner as soon as it opens.
 * - `after-answer`: banner dismissed, the assistant is still open.
 * - `quota`: back on the home page, the bubble points at the counter.
 */
export type AiOnboardingStep = 'search' | 'answer' | 'after-answer' | 'quota';

export interface AiOnboardingState {
  step: AiOnboardingStep | null;
  /** The counter bubble opened from the counter itself, outside the tour. */
  standalone?: boolean;
}

/**
 * Shows the assistant's guided tour once per account: it starts the first
 * time the home page is shown with the assistant usable, and ends for good
 * as soon as it is completed, skipped or interrupted. Completion is stored on
 * the account (API) and on the device, so neither a reinstall nor a slow
 * network brings it back.
 */
@Injectable({
  providedIn: 'root',
})
export class AiOnboardingService {
  /** Sent as is; the API only exempts this exact question from the quota. */
  static readonly SAMPLE_QUESTION = 'Puis-je débloquer ma participation pour acheter un vélo électrique ?';

  private stateSubject = new BehaviorSubject<AiOnboardingState>({ step: null });
  state$ = this.stateSubject.asObservable();

  /** Device-side record, per account: `{uuid: isoDate}`. */
  private readonly storageKey = `${environment.application}-ai-onboarding-done`;
  private localDoneSubject = new BehaviorSubject<Record<string, string> | undefined>(undefined);
  private initialized = false;
  private done = false;
  private triggerSubscription?: Subscription;
  private overlaySubscription?: Subscription;

  constructor(
    private store: Store,
    private router: Router,
    private aiService: AiService,
    private aiOverlayService: AiOverlayService,
    private searchStateService: SearchStateService,
  ) {}

  get step(): AiOnboardingStep | null {
    return this.stateSubject.value.step;
  }

  get standalone(): boolean {
    return !!this.stateSubject.value.standalone;
  }

  /** A tap on the counter: its explanation, any time, without the tour. */
  showQuotaInfo(): void {
    if (this.step !== null || !this.showsQuotaStep) {
      return;
    }

    this.setStep('quota', true);
  }

  /** "C'est compris" on the counter bubble: ends the tour, or just closes the explanation. */
  closeQuotaStep(): void {
    if (this.standalone) {
      this.setStep(null);
    } else {
      this.finish();
    }
  }

  /** Called once, by the component that renders the tour. */
  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.localDoneSubject.next(await this.readLocalDone());
    this.watchForStart();
    this.overlaySubscription = this.aiOverlayService.overlay$.subscribe(({ isOpen, onboarding }) => {
      if (!isOpen) {
        this.onOverlayClosed();
      } else if (!onboarding && this.step === 'search') {
        // The assistant opened by another way (a link in an article): the
        // reader is already using it, no need to keep the first bubble.
        this.finish();
      }
    });
  }

  /**
   * The tour starts the first time the home page is displayed with the
   * assistant usable: tour still due on the account, domain and role allow
   * it, quota known and not used up, no search or assistant in progress.
   *
   * A notification can take the reader away from the home page right after
   * the first bubble appeared (the app opens on the home page, then jumps to
   * the article). The bubble is then withdrawn, nothing is counted, and it
   * comes back the next time the home page is shown.
   */
  private watchForStart(): void {
    const url$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects || event.url),
      startWith(this.router.url),
    );

    this.triggerSubscription = combineLatest([
      this.store.select(selectUser),
      this.store.select(selectDomain),
      this.store.select(selectRoles),
      this.aiService.quota$,
      this.searchStateService.searchActive$,
      url$,
      this.localDoneSubject,
    ]).subscribe(([user, domain, roles, quota, searchActive, url, localDone]: [User, Domain, string[], AiQuotaStatus | undefined, boolean, string, Record<string, string> | undefined]) => {
      if (this.step === 'search' && !this.isHomeUrl(url)) {
        this.setStep(null);
        return;
      }

      if (this.shouldStart(user, domain, roles, quota, searchActive, url, localDone)) {
        this.setStep('search');
      }
    });
  }

  private isHomeUrl(url: string): boolean {
    const path = url.split('?')[0];

    return path === '/news' || path === '/news-bis';
  }

  private shouldStart(
    user: User | null,
    domain: Domain | null,
    roles: string[],
    quota: AiQuotaStatus | undefined,
    searchActive: boolean,
    url: string,
    localDone: Record<string, string> | undefined,
  ): boolean {
    if (this.done || this.step !== null) {
      return false;
    }

    if (!user?.uuid) {
      return false;
    }

    // `undefined` means an API without the tour: never start on a guess.
    if (user.ai_onboarding_completed_at !== null) {
      return false;
    }

    // Completed on this device but the profile has not caught up yet.
    if (!localDone || localDone[user.uuid]) {
      return false;
    }

    if (!canUseAiAssistant(domain, roles) || !quota || quota.exceeded) {
      return false;
    }

    if (searchActive || this.aiOverlayService.isOpen) {
      return false;
    }

    return this.isHomeUrl(url);
  }

  /**
   * "Répondre à cette question": the sample question goes to the assistant,
   * which shows the banner right away while the answer is being written.
   */
  testQuestion(): void {
    if (this.step !== 'search') {
      return;
    }

    this.setStep('answer');
    this.aiOverlayService.open(AiOnboardingService.SAMPLE_QUESTION, { onboarding: true });
  }

  /** The banner's cross: the counter step waits for the assistant to close. */
  dismissAnswerBubble(): void {
    if (this.step === 'answer') {
      this.setStep('after-answer');
    }
  }

  /** "Passer", the cross, or the last "C'est compris": the tour is over. */
  finish(): void {
    if (this.done) {
      return;
    }

    this.done = true;
    this.triggerSubscription?.unsubscribe();
    this.triggerSubscription = undefined;
    this.setStep(null);

    this.rememberLocalDone();
    this.aiService.completeOnboarding().subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /** Steps shown in the bubbles' "x sur y": the counter step only exists for metered users. */
  get totalSteps(): number {
    return this.showsQuotaStep ? 3 : 2;
  }

  get showsQuotaStep(): boolean {
    return !!this.aiService.quota?.metered;
  }

  private onOverlayClosed(): void {
    switch (this.step) {
      case 'answer':
      case 'after-answer':
        if (this.showsQuotaStep) {
          this.setStep('quota');
        } else {
          this.finish();
        }
        break;
      default:
        break;
    }
  }

  private setStep(step: AiOnboardingStep | null, standalone = false): void {
    this.stateSubject.next({ step, standalone });
  }

  private async readLocalDone(): Promise<Record<string, string>> {
    try {
      const stored = await Preferences.get({ key: this.storageKey });
      const parsed = stored.value ? JSON.parse(stored.value) : {};

      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  private rememberLocalDone(): void {
    this.store.select(selectUser).pipe(take(1)).subscribe((user: User | null) => {
      if (!user?.uuid) {
        return;
      }

      const done = { ...(this.localDoneSubject.value || {}), [user.uuid]: new Date().toISOString() };
      this.localDoneSubject.next(done);
      Preferences.set({ key: this.storageKey, value: JSON.stringify(done) }).catch(() => {});
    });
  }
}
